/* Allpowers Rebuild — theme.js (vanilla, no dependencies) */
(() => {
  "use strict";

  /* ---------- Rotator: shared slide logic (announcement, slideshow, showcase) ---------- */
  class SlideRotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll("[data-slide]"));
      this.dots = Array.from(this.querySelectorAll("[data-dot]"));
      this.index = 0;
      this.autoplay = parseInt(this.dataset.autoplay || "0", 10);
      this.querySelectorAll("[data-prev]").forEach((b) => b.addEventListener("click", () => this.go(this.index - 1)));
      this.querySelectorAll("[data-next]").forEach((b) => b.addEventListener("click", () => this.go(this.index + 1)));
      this.dots.forEach((d, i) => d.addEventListener("click", () => this.go(i)));
      this.render();
      if (this.autoplay > 0 && this.slides.length > 1) this.start();
      this.addEventListener("mouseenter", () => this.stop());
      this.addEventListener("mouseleave", () => this.autoplay > 0 && this.start());
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); this.timer = setInterval(() => this.go(this.index + 1), this.autoplay); }
    stop() { if (this.timer) clearInterval(this.timer); this.timer = null; }
    go(i) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.render();
    }
    render() {
      this.slides.forEach((s, i) => s.classList.toggle("is-active", i === this.index));
      this.dots.forEach((d, i) => d.classList.toggle("is-active", i === this.index));
    }
  }
  customElements.define("slide-rotator", SlideRotator);

  /* ---------- Drawers (nav / cart / search) ---------- */
  class DrawerDialog extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector(".drawer__overlay");
      if (this.overlay) this.overlay.addEventListener("click", () => this.close());
      this.querySelectorAll("[data-drawer-close]").forEach((b) => b.addEventListener("click", () => this.close()));
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.close(); });
    }
    open() {
      this.classList.add("is-open");
      document.body.style.overflow = "hidden";
      const focusable = this.querySelector("input, button:not([data-drawer-close])");
      if (focusable) setTimeout(() => focusable.focus(), 250);
    }
    close() {
      this.classList.remove("is-open");
      document.body.style.overflow = "";
    }
  }
  customElements.define("drawer-dialog", DrawerDialog);

  document.addEventListener("click", (e) => {
    const closer = e.target.closest("[data-drawer-close]");
    if (closer) {
      const drawer = closer.closest("drawer-dialog");
      if (drawer && typeof drawer.close === "function") drawer.close();
      return;
    }
    const opener = e.target.closest("[data-drawer-open]");
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === "function") {
      e.preventDefault();
      drawer.open();
    }
  });

  /* ---------- Desktop nav (hover intent) ---------- */
  const headerEl = document.querySelector(".site-header");
  const placeMega = () => {
    if (!headerEl) return;
    document.documentElement.style.setProperty("--mega-top", `${Math.round(headerEl.getBoundingClientRect().bottom)}px`);
  };
  placeMega();
  window.addEventListener("resize", placeMega, { passive: true });
  window.addEventListener("scroll", placeMega, { passive: true });
  document.querySelectorAll("[data-nav-item]").forEach((item) => {
    let t;
    item.addEventListener("mouseenter", () => { clearTimeout(t); placeMega(); item.classList.add("is-open"); });
    item.addEventListener("mouseleave", () => { t = setTimeout(() => item.classList.remove("is-open"), 120); });
    const link = item.querySelector(":scope > .nav-link");
    if (link) link.addEventListener("focus", () => { placeMega(); item.classList.add("is-open"); });
  });

  /* ---------- Mobile nav accordions ---------- */
  document.querySelectorAll("[data-mobile-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => btn.closest(".mobile-nav__item").classList.toggle("is-open"));
  });

  /* ---------- Quantity selector ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("input");
      this.querySelector("[data-minus]").addEventListener("click", () => this.step(-1));
      this.querySelector("[data-plus]").addEventListener("click", () => this.step(1));
    }
    step(d) {
      const v = Math.max(parseInt(this.input.min || "1", 10), (parseInt(this.input.value, 10) || 1) + d);
      this.input.value = v;
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  customElements.define("quantity-input", QuantityInput);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-media-track]");
      this.items = Array.from(this.querySelectorAll("[data-media-item]"));
      this.thumbs = Array.from(this.querySelectorAll("[data-thumb]"));
      this.dots = Array.from(this.querySelectorAll("[data-gallery-dot]"));
      this.thumbs.forEach((th, i) => th.addEventListener("click", () => this.show(i)));
      if (this.track) {
        this.track.addEventListener("scroll", () => {
          clearTimeout(this.scrollT);
          this.scrollT = setTimeout(() => {
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            this.setActive(i);
          }, 80);
        }, { passive: true });
      }
      this.setActive(0);
    }
    show(i) {
      if (this.track) this.track.scrollTo({ left: i * this.track.clientWidth, behavior: "smooth" });
      this.setActive(i);
    }
    setActive(i) {
      this.thumbs.forEach((t, k) => t.classList.toggle("is-active", k === i));
      this.dots.forEach((d, k) => d.classList.toggle("is-active", k === i));
    }
    /* jump to variant featured media */
    showMediaId(id) {
      const i = this.items.findIndex((el) => el.dataset.mediaId === String(id));
      if (i >= 0) this.show(i);
    }
  }
  customElements.define("product-gallery", ProductGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector("[data-variants-json]");
      if (!dataEl) return;
      this.variants = JSON.parse(dataEl.textContent);
      this.section = this.closest("[data-product-section]");
      this.addEventListener("change", () => this.onChange());
    }
    selectedOptions() {
      const opts = [];
      this.querySelectorAll("fieldset").forEach((fs) => {
        const checked = fs.querySelector("input:checked");
        const select = fs.querySelector("select");
        opts.push(checked ? checked.value : select ? select.value : null);
      });
      return opts;
    }
    onChange() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) =>
        opts.every((o, i) => !o || v.options[i] === o)
      );
      if (!variant) return;
      const idInput = this.section.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;
      /* price */
      const priceEl = this.section.querySelector("[data-price]");
      if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;
      /* availability */
      const atc = this.section.querySelector("[data-atc]");
      if (atc) {
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? atc.dataset.labelAdd : atc.dataset.labelSoldout;
      }
      /* media */
      const gallery = this.section.querySelector("product-gallery");
      if (gallery && variant.featured_media_id) gallery.showMediaId(variant.featured_media_id);
      /* URL */
      const url = new URL(location.href);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url);
    }
  }
  customElements.define("variant-picker", VariantPicker);

  /* ---------- Cart API helpers ---------- */
  const Cart = {
    async add(form) {
      const res = await fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart/add.js" : "/cart/add.js", {
        method: "POST",
        body: new FormData(form),
      });
      if (!res.ok) throw await res.json();
      return res.json();
    },
    async change(key, quantity) {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      return res.json();
    },
    sectionId(el) {
      const section = el && el.closest(".shopify-section");
      return section ? section.id.replace(/^shopify-section-/, "") : "";
    },
    async refreshDrawer() {
      const drawer = document.getElementById("CartDrawer");
      const header = document.querySelector(".site-header");
      const ids = [this.sectionId(drawer), this.sectionId(header)].filter(Boolean);
      if (!ids.length) return;
      const res = await fetch(`/?sections=${ids.join(",")}`);
      if (!res.ok) return;
      const json = await res.json();
      const drawerId = this.sectionId(drawer);
      const drawerHtml = drawerId && json[drawerId];
      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, "text/html");
        const fresh = doc.querySelector("#CartDrawer .drawer__panel") || doc.querySelector(".drawer__panel");
        const current = document.querySelector("#CartDrawer .drawer__panel");
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
      const headerId = this.sectionId(header);
      const headerHtml = headerId && json[headerId];
      const countSrc = headerHtml
        ? new DOMParser().parseFromString(headerHtml, "text/html").querySelector("[data-cart-count]")
        : null;
      if (countSrc) {
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
          el.textContent = countSrc.textContent;
        });
      }
    },
  };

  /* product form submit -> add to cart, open drawer */
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector("[data-atc]");
    const orig = btn ? btn.textContent : "";
    if (btn) { btn.disabled = true; btn.textContent = btn.dataset.labelAdding || "Adding…"; }
    try {
      await Cart.add(form);
      await Cart.refreshDrawer();
      const drawer = document.getElementById("CartDrawer");
      if (drawer && typeof drawer.open === "function") drawer.open();
    } catch (err) {
      alert((err && err.description) || "Unable to add to cart");
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = orig; }
    }
  });

  /* cart line quantity / remove (drawer + page) */
  document.addEventListener("change", async (e) => {
    const qty = e.target.closest("[data-line-qty]");
    if (!qty) return;
    await Cart.change(qty.dataset.lineKey, parseInt(qty.value, 10) || 0);
    if (qty.closest("#CartDrawer")) Cart.refreshDrawer();
    else location.reload();
  });
  document.addEventListener("click", async (e) => {
    const rm = e.target.closest("[data-line-remove]");
    if (!rm) return;
    e.preventDefault();
    await Cart.change(rm.dataset.lineKey, 0);
    if (rm.closest("#CartDrawer")) Cart.refreshDrawer();
    else location.reload();
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector("[data-results]");
      if (!this.input) return;
      this.input.addEventListener("input", () => {
        clearTimeout(this.t);
        this.t = setTimeout(() => this.search(), 250);
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ""; return; }
      const res = await fetch(`/search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,article&resources[limit]=6&section_id=predictive-search`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const inner = doc.querySelector("#predictive-results");
      this.results.innerHTML = inner ? inner.innerHTML : "";
    }
  }
  customElements.define("predictive-search", PredictiveSearch);

  /* ---------- Collection sort / filter ---------- */
  document.addEventListener("change", (e) => {
    const sort = e.target.closest("[data-sort-by]");
    if (sort) {
      const url = new URL(location.href);
      url.searchParams.set("sort_by", sort.value);
      location.href = url.toString();
    }
    const facetForm = e.target.closest("form[data-facet-form]");
    if (facetForm) facetForm.submit();
  });

  /* ---------- Product spec "View all" ---------- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-spec-more]");
    if (!btn) return;
    const box = btn.closest(".feature-spec");
    if (!box) return;
    const open = box.classList.toggle("is-open");
    const label = btn.querySelector("[data-spec-more-label]");
    if (label) label.textContent = open ? "View less" : "View all";
  });

  /* ---------- Product detail story (HTML metafield) ---------- */
  document.querySelectorAll("[data-product-story]").forEach((el) => {
    const src = document.querySelector("[data-product-story-json]");
    if (!src) return;
    try {
      const html = JSON.parse(src.textContent || '""');
      if (typeof html === "string" && html.trim()) el.innerHTML = html;
    } catch (err) { /* keep empty */ }
    el.querySelectorAll("img[data-src]").forEach((img) => {
      if (!img.getAttribute("src")) img.src = img.getAttribute("data-src");
    });
    el.querySelectorAll("video[data-src]").forEach((video) => {
      if (!video.getAttribute("src")) video.src = video.getAttribute("data-src");
    });
    el.querySelectorAll(".pf-bg-lazy").forEach((node) => node.classList.remove("pf-bg-lazy"));
  });

  /* ---------- Newsletter popup ---------- */
  const popup = document.querySelector("[data-popup]");
  if (popup && !localStorage.getItem("popup-dismissed")) {
    setTimeout(() => popup.classList.add("is-open"), parseInt(popup.dataset.delay || "5000", 10));
    popup.querySelectorAll("[data-drawer-close], .drawer__overlay").forEach((el) =>
      el.addEventListener("click", () => localStorage.setItem("popup-dismissed", "1"))
    );
  }
})();
